/**
 * API service for backend communication
 */

// Use environment variable for API URL in production, or proxy in development
const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

/**
 * Generate lottery numbers
 * @param {string} model - Generation model
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Generated numbers
 */
export async function generateNumbers(model, options = {}) {
  try {
    const response = await fetch(`${API_BASE}/lotto/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ model, options })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to generate numbers');
    }

    return data;
  } catch (error) {
    console.error('API Error (generateNumbers):', error);
    throw error;
  }
}

/**
 * Get latest draw
 * @returns {Promise<Object>} Latest draw data
 */
export async function getLatestDraw() {
  try {
    const response = await fetch(`${API_BASE}/lotto/latest`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to get latest draw');
    }

    return data;
  } catch (error) {
    console.error('API Error (getLatestDraw):', error);
    throw error;
  }
}

/**
 * Get specific draw by draw number
 * @param {number} drawNo - Draw number
 * @returns {Promise<Object>} Draw data
 */
export async function getDrawByNumber(drawNo) {
  try {
    const response = await fetch(`${API_BASE}/lotto/draw/${drawNo}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to get draw');
    }

    return data;
  } catch (error) {
    console.error('API Error (getDrawByNumber):', error);
    throw error;
  }
}

/**
 * Get draw history
 * @param {number} limit - Number of draws to fetch
 * @param {number} offset - Offset for pagination
 * @returns {Promise<Object>} Draw history
 */
export async function getDrawHistory(limit = 20, offset = 0) {
  try {
    const response = await fetch(`${API_BASE}/lotto/history?limit=${limit}&offset=${offset}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to get draw history');
    }

    return data;
  } catch (error) {
    console.error('API Error (getDrawHistory):', error);
    throw error;
  }
}

/**
 * Get statistics
 * @param {number} range - Number of recent draws to analyze
 * @returns {Promise<Object>} Statistics data
 */
export async function getStatistics(range = 100) {
  try {
    const response = await fetch(`${API_BASE}/statistics?range=${range}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to get statistics');
    }

    return data;
  } catch (error) {
    console.error('API Error (getStatistics):', error);
    throw error;
  }
}

// Fortune API functions

/**
 * Get zodiac fortune for today
 * @param {string} sign - Zodiac sign (aries, taurus, etc.)
 * @returns {Promise<Object>} Zodiac fortune data
 */
export async function getZodiacFortune(sign) {
  try {
    const response = await fetch(`${API_BASE}/fortune/zodiac/${sign}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get zodiac fortune');
    }

    return data;
  } catch (error) {
    console.error('API Error (getZodiacFortune):', error);
    throw error;
  }
}

/**
 * Get Chinese zodiac fortune for today
 * @param {string} sign - Chinese zodiac sign (rat, ox, etc.)
 * @returns {Promise<Object>} Chinese zodiac fortune data
 */
export async function getChineseFortune(sign) {
  try {
    const response = await fetch(`${API_BASE}/fortune/chinese/${sign}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get Chinese fortune');
    }

    return data;
  } catch (error) {
    console.error('API Error (getChineseFortune):', error);
    throw error;
  }
}

/**
 * Get tarot card fortune
 * @param {number} cardId - Tarot card ID (0-21)
 * @returns {Promise<Object>} Tarot card fortune data
 */
export async function getTarotCard(cardId) {
  try {
    const response = await fetch(`${API_BASE}/fortune/tarot/${cardId}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get tarot card');
    }

    return data;
  } catch (error) {
    console.error('API Error (getTarotCard):', error);
    throw error;
  }
}

/**
 * Get today's lucky fortune
 * @returns {Promise<Object>} Lucky fortune data
 */
export async function getLuckyToday() {
  try {
    const response = await fetch(`${API_BASE}/fortune/lucky`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get lucky fortune');
    }

    return data;
  } catch (error) {
    console.error('API Error (getLuckyToday):', error);
    throw error;
  }
}

/**
 * Get dream fortune by category
 * @param {string} category - Dream category (animal, nature, etc.)
 * @returns {Promise<Object>} Dream fortune data
 */
export async function getDreamFortune(category) {
  try {
    const response = await fetch(`${API_BASE}/fortune/dream/${category}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get dream fortune');
    }

    return data;
  } catch (error) {
    console.error('API Error (getDreamFortune):', error);
    throw error;
  }
}

/**
 * Interpret a free-form dream
 * @param {string} dream - Dream description text
 * @returns {Promise<Object>} Dream interpretation data
 */
export async function interpretDream(dream) {
  try {
    const response = await fetch(`${API_BASE}/fortune/dream/interpret`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dream })
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to interpret dream');
    }

    return data;
  } catch (error) {
    console.error('API Error (interpretDream):', error);
    throw error;
  }
}

/**
 * Get all today's fortunes
 * @returns {Promise<Object>} All fortunes for today
 */
export async function getAllTodayFortunes() {
  try {
    const response = await fetch(`${API_BASE}/fortune/today`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get today\'s fortunes');
    }

    return data;
  } catch (error) {
    console.error('API Error (getAllTodayFortunes):', error);
    throw error;
  }
}

/**
 * Get blog posts with pagination
 * @param {number} page - Page number
 * @param {number} limit - Posts per page
 * @returns {Promise<Object>} Blog posts data
 */
export async function getBlogPosts(page = 1, limit = 20) {
  try {
    const response = await fetch(`${API_BASE}/blog/posts?page=${page}&limit=${limit}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to get blog posts');
    }

    return data;
  } catch (error) {
    console.error('API Error (getBlogPosts):', error);
    throw error;
  }
}

/**
 * Get blog post by slug
 * @param {string} slug - Post slug
 * @returns {Promise<Object>} Blog post data
 */
export async function getBlogPostBySlug(slug) {
  try {
    const response = await fetch(`${API_BASE}/blog/posts/${slug}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Post not found');
    }

    return data;
  } catch (error) {
    console.error('API Error (getBlogPostBySlug):', error);
    throw error;
  }
}
